import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';
import 'react-drawer/lib/react-drawer.css';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import {
  Button, UncontrolledDropdown, DropdownToggle, DropdownMenu, DropdownItem,
} from 'reactstrap';
import { withTranslation } from 'react-i18next';
import ProfileMenu from '../CommonForBoth/TopbarDropdown/ProfileMenu';
import LanguageDropdown from '../CommonForBoth/TopbarDropdown/LanguageDropdown';
import ZebraDropwdown from '../CommonForBoth/TopbarDropdown/ZebraDropdown';
import logoDarkLg from '../../assets/images/DropzoneLogo.png';
import logoLightSm from '../../assets/images/logo-sm-light.png';
import logoLightDarkSm from '../../assets/images/logo-dark.png';
import logoLightLg from '../../assets/images/dropzone-Logo-dark.png';
import NotificationDropdown from '../CommonForBoth/TopbarDropdown/NotificationDropdown';
import {
  toggleRightSidebar,
  changeSidebarType as changeSidebarTypeAction,
  changeLayoutMode as changeLayoutModeAction,
  changeSidebarTheme as changeSidebarThemeAction,
  changeTopbarTheme as changeTopbarThemeAction,
} from '../../store/actions';
import { useScanner } from '../../utilities/ScannerContext';
import { layoutModeTypes, leftSideBarThemeTypes, topBarThemeTypes } from '../../constants/layout';

function Header({
  t,
  toggleMenuCallback,
  leftSideBarType,
  layoutModeType,
  changeSidebarType,
  changeLayoutMode,
  changeSidebarTheme,
  changeTopbarTheme,
}) {
  const [isSearch, setIsSearch] = useState(false);
  const [scannerDisconnecting, setScannerDisconnecting] = useState(false);

  const {
    reconnectScanner, connectScanner, destroyReader, isConnected,
  } = useScanner();
  const hasScanner = Boolean(isConnected());

  useEffect(() => {
    reconnectScanner();
  }, []);

  const handleScannerConnect = (transport) => {
    connectScanner(null, transport);
  };

  const handleScannerDisconnect = async () => {
    setScannerDisconnecting(true);
    try {
      await destroyReader();
    } finally {
      setScannerDisconnecting(false);
    }
  };

  const toggleMenu = () => {
    toggleMenuCallback();
    changeSidebarType(leftSideBarType === 'default' ? 'icon' : 'default');
  };

  const isDarkMode = layoutModeType === layoutModeTypes.DARK;

  const handleThemeToggle = () => {
    const nextIsDark = !isDarkMode;

    changeLayoutMode(nextIsDark ? layoutModeTypes.DARK : layoutModeTypes.LIGHT);
    changeSidebarTheme(nextIsDark ? leftSideBarThemeTypes.DARK : leftSideBarThemeTypes.LIGHT);
    changeTopbarTheme(nextIsDark ? topBarThemeTypes.DARK : topBarThemeTypes.LIGHT);
  };

  return (
    <header id="page-topbar">
      <div className="navbar-header">
        <div className="d-flex">
          <div className="navbar-brand-box">
            <Link to="/" className="logo logo-dark">
              <span className="logo-sm">
                <img src={logoLightSm} alt="" height="41" />
              </span>
              <span className="logo-lg">
                <img src={logoDarkLg} alt="" height="48" />
              </span>
            </Link>

            <Link to="/" className="logo logo-light">
              <span className="logo-sm">
                <img src={logoLightDarkSm} alt="" height="41" />
              </span>
              <span className="logo-lg">
                <img src={logoLightLg} alt="" height="48" />
              </span>
            </Link>
          </div>

          <button
            type="button"
            onClick={toggleMenu}
            className="btn btn-sm px-3 font-size-16 header-item"
            id="vertical-menu-btn"
          >
            <i className="fa fa-fw fa-bars" />
          </button>
        </div>

        <div className="d-flex">
          <div>
            <Button
              color="default"
              className="btn btn-sm px-3 font-size-16 header-item theme-toggle-button"
              onClick={handleThemeToggle}
              title={isDarkMode ? t('Switch to light mode') : t('Switch to dark mode')}
              aria-label={isDarkMode ? t('Switch to light mode') : t('Switch to dark mode')}
            >
              <i
                style={{ fontSize: '21px' }}
                className={`fa fa-fw mdi ${isDarkMode ? 'mdi-weather-sunny' : 'mdi-weather-night'}`}
              />
            </Button>
          </div>
          <div>
            {hasScanner && (
              <Button
                color="default"
                className="btn btn-sm px-3 font-size-16 header-item text-success"
                onClick={handleScannerDisconnect}
                disabled={scannerDisconnecting}
                title={t('Disconnect Barcode Scanner')}
                aria-label={t('Disconnect Barcode Scanner')}
              >
                <i
                  style={{ fontSize: '21px' }}
                  className={`fa fa-fw mdi ${scannerDisconnecting ? 'mdi-loading mdi-spin' : 'mdi-barcode'}`}
                />
              </Button>
            )}
            {!hasScanner && (
              <UncontrolledDropdown>
                <DropdownToggle
                  color="default"
                  className="btn btn-sm px-3 font-size-16 header-item text-dark"
                  title={t('Connect Barcode Scanner')}
                >
                  <i style={{ fontSize: '21px' }} className="fa fa-fw mdi mdi-barcode-off" />
                </DropdownToggle>
                <DropdownMenu className="dropdown-menu-end">
                  <DropdownItem header>{t('Connect Barcode Scanner')}</DropdownItem>
                  <DropdownItem onClick={() => handleScannerConnect('hid')}>
                    <i className="mdi mdi-usb me-2" />
                    {t('USB HID scanner')}
                  </DropdownItem>
                  <DropdownItem onClick={() => handleScannerConnect('serial')}>
                    <i className="mdi mdi-serial-port me-2" />
                    {t('USB Serial scanner (COM)')}
                  </DropdownItem>
                </DropdownMenu>
              </UncontrolledDropdown>
            )}
          </div>
          <ZebraDropwdown />
          <LanguageDropdown />

          <div className="dropdown d-inline-block d-lg-none ms-2">
            <button
              onClick={() => setIsSearch(!isSearch)}
              type="button"
              className="btn header-item noti-icon"
              id="page-header-search-dropdown"
            >
              <i className="mdi mdi-magnify" />
            </button>
            <div
              className={
                isSearch
                  ? 'dropdown-menu dropdown-menu-lg dropdown-menu-end p-0 show'
                  : 'dropdown-menu dropdown-menu-lg dropdown-menu-end p-0'
              }
              aria-labelledby="page-header-search-dropdown"
            >
              <form className="p-3">
                <div className="form-group m-0">
                  <div className="input-group">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Search anything..."
                    />
                    <div className="input-group-append">
                      <button className="btn btn-primary" type="submit">
                        <i className="mdi mdi-magnify" />
                      </button>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>

          <NotificationDropdown />
          <ProfileMenu />
        </div>
      </div>
    </header>
  );
}

Header.defaultProps = {
  t: null,
  leftSideBarType: 'default',
  layoutModeType: layoutModeTypes.LIGHT,
};

Header.propTypes = {
  t: PropTypes.any,
  toggleMenuCallback: PropTypes.func.isRequired,
  leftSideBarType: PropTypes.string,
  layoutModeType: PropTypes.string,
  changeSidebarType: PropTypes.func.isRequired,
  changeLayoutMode: PropTypes.func.isRequired,
  changeSidebarTheme: PropTypes.func.isRequired,
  changeTopbarTheme: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => {
  const {
    layoutType,
    showRightSidebar,
    leftSideBarType,
    layoutModeType,
  } = state.Layout;
  return {
    layoutType,
    showRightSidebar,
    leftSideBarType,
    layoutModeType,
  };
};

export default connect(mapStateToProps, {
  toggleRightSidebar,
  changeSidebarType: changeSidebarTypeAction,
  changeLayoutMode: changeLayoutModeAction,
  changeSidebarTheme: changeSidebarThemeAction,
  changeTopbarTheme: changeTopbarThemeAction,
})(withTranslation()(Header));
